import { showDialogInfo } from "@/hooks/use-dialog-info"
import { ItineraryEvent } from "@/types/itinerary"

import { toastManager } from "@/lib/toast"
import { exists } from "@/lib/utils/presence"
import { isSerializedClientError } from "./storage/api/base-query"

type ConfirmationRequiredData = {
  movable_event_ids: number[]
  extendable_event_ids: number[]
}

function getItineraryExceptionEventIds(err: unknown): number[] | undefined {
  if (isSerializedClientError(err, "ConfirmationRequired")) {
    const data = err.data as ConfirmationRequiredData
    return [
      ...(data.movable_event_ids ?? []),
      ...(data.extendable_event_ids ?? []),
    ]
  }
  if (
    isSerializedClientError(err, "ImmovableItineraryEvents") ||
    isSerializedClientError(err, "ItineraryEventsPreventFlexibleTrip") ||
    isSerializedClientError(err, "UndeletableItineraryEvents") ||
    isSerializedClientError(err, "ItineraryEventOutsideTimeFrame")
  ) {
    return err.data as number[]
  }
  return undefined
}

function getItineraryExceptionType(err: unknown): string | undefined {
  if (
    isSerializedClientError(err, "ImmovableItineraryEvents") ||
    isSerializedClientError(err, "ConfirmationRequired") ||
    isSerializedClientError(err, "ItineraryEventsPreventFlexibleTrip") ||
    isSerializedClientError(err, "UndeletableItineraryEvents") ||
    isSerializedClientError(err, "ItineraryEventOutsideTimeFrame")
  ) {
    return err.name
  }
  return undefined
}

export function handleTripUpdateError(
  err: unknown,
  allItineraryEvents?: ItineraryEvent[]
) {
  const errorType = getItineraryExceptionType(err)
  const ids = getItineraryExceptionEventIds(err)

  if (!errorType || !ids) {
    toastManager.add({ type: "error", description: "Failed to update trip." })
    return
  }

  const badEvents = ids
    .map((id) => allItineraryEvents?.find((event) => event.id === id))
    .filter(exists)

  let base: string
  switch (errorType) {
    case "UndeletableItineraryEvents":
      base = "Some of the events in your itinerary can’t be removed."
      break
    case "ItineraryEventsPreventFlexibleTrip":
      base =
        "This trip can’t have a flexible start date because some of the events are scheduled for specific dates."
      break
    default:
      base = "Some of the events in your itinerary can’t be moved."
  }
  const message = badEvents.length ? (
    <>
      {base} Check your itinerary for the following:{" "}
      <span className="no-translate">
        {badEvents.map((event) => event.title).join(", ")}
      </span>
      .
    </>
  ) : (
    base
  )

  showDialogInfo({
    title: "Check your itinerary",
    message,
  })
}
